// ── Docmorphia Design System — JS token module ──────────────────────────
// All inline-styled components import from here.
// Keys that existed before are preserved so consumers auto-retint.

// ── Brand palette ────────────────────────────────────────────────────────
export const teal = {
  300: 'var(--teal-300)',
  500: 'var(--teal-500)',
  600: 'var(--teal-600)',
}

// ── Colors ───────────────────────────────────────────────────────────────
export const colors = {
  // Brand
  primary:       'var(--color-primary)',
  primaryHover:  'var(--color-primary-hover)',
  primaryLight:  'var(--color-primary-light)',
  primarySubtle: 'var(--color-primary-subtle)',

  // Surfaces
  background: 'var(--color-bg)',        // app bg (outer shell)
  surface:    'var(--color-surface)',   // card / panel bg
  surfaceTinted: 'var(--color-surface-tinted)',
  surfaceMist:   'var(--color-surface-mist)',

  // Text
  text:          '#0A1413',  // ink-900
  textBody:      '#171A22',  // ink-800
  textSecondary: '#6B7180',  // slate-500
  textMuted:     '#9AA0AD',  // slate-400

  // Borders
  border:      'var(--color-border)',  // cloud-200
  borderFocus: 'var(--color-primary)',

  // Feedback — success
  success:      '#166534',
  successLight: '#F0FDF4',
  successBorder:'#BBF7D0',

  // Feedback — warning
  warning:      '#92400E',
  warningLight: '#FFF7ED',
  warningBorder:'#FED7AA',

  // Feedback — error / danger
  error:        '#B91C1C',
  errorLight:   '#FEF2F2',
  errorBorder:  '#FECACA',

  // Feedback — info
  info:         '#1E40AF',
  infoLight:    '#EFF6FF',
  infoBorder:   '#BFDBFE',

  // On-color
  onPrimary: '#FFFFFF',  // white text on teal backgrounds

  // Overlay
  overlay: 'rgba(10,20,19,0.45)',  // modal backdrop

  // Sidebar (now light)
  sidebarBg:         '#FFFFFF',
  sidebarText:       '#6B7180',
  sidebarTextHover:  '#0A1413',
  sidebarHover:      'var(--color-surface-mist)',
  sidebarActive:     'var(--color-primary)',
  sidebarActiveBg:   'var(--color-primary-light)',
  sidebarBorder:     '#EEF0F1',
  sidebarSectionLabel: '#9AA0AD',
}

// ── Feedback triplets (bg / text / border) ──────────────────────────────
export const feedback = {
  success: { bg: '#F0FDF4', text: '#166534', border: '#BBF7D0' },
  warning: { bg: '#FFF7ED', text: '#92400E', border: '#FED7AA' },
  danger:  { bg: '#FEF2F2', text: '#B91C1C', border: '#FECACA' },
  info:    { bg: '#EFF6FF', text: '#1E40AF', border: '#BFDBFE' },
}

// ── Status chips (template status, pill shape) ───────────────────────────
export const statusChip = {
  active:   { bg: '#0F9D92', text: '#FFFFFF', border: 'transparent' },
  draft:    { bg: '#EEF0F1', text: '#6B7180', border: '#EEF0F1' },
  archived: { bg: '#FFF7ED', text: '#92400E', border: '#FED7AA' },
  deleted:  { bg: '#FEF2F2', text: '#B91C1C', border: '#FECACA' },
}

// ── Version chips (tag shape) ─────────────────────────────────────────────
export const versionChip = {
  active:   { bg: '#F4FAF9', text: '#0F9D92', border: '#5FD9CF', dot: '#0F9D92' },
  inactive: { bg: 'transparent', text: '#9AA0AD', border: '#EEF0F1', dot: '#9AA0AD' },
}

// ── Role chips ────────────────────────────────────────────────────────────
export const roleChip = {
  'super-admin': { bg: '#0A1413', text: '#FFFFFF', border: '#0A1413' },
  'team-admin':  { bg: 'transparent', text: '#0A1413', border: '#0A1413' },
  editor: { bg: 'transparent', text: '#0F9D92', border: '#5FD9CF' },
  viewer: { bg: 'transparent', text: '#6B7180', border: '#EEF0F1' },
}

// ── User status chips ─────────────────────────────────────────────────────
export const userStatusChip = {
  active:    { bg: '#0F9D92', text: '#FFFFFF' },
  invited:   { bg: '#FFF7ED', text: '#92400E', border: '#FED7AA' },
  suspended: { bg: 'transparent', text: '#9AA0AD' },
}

// ── Typography ────────────────────────────────────────────────────────────
export const fontFamily = {
  sans: "'Outfit', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  mono: "'JetBrains Mono', 'Fira Code', 'Cascadia Code', monospace",
}

export const fontSize = {
  // Design scale (preferred names)
  '2xs':  '10px',
  xs:     '11px',
  sm:     '13px',
  base:   '14px',
  md:     '15px',
  lg:     '17px',
  xl:     '20px',
  '2xl':  '24px',
  '3xl':  '28px',
  '4xl':  '34px',
  '5xl':  '42px',
  // Legacy aliases (kept so existing consumers compile)
  xxl:    '32px',
}

export const fontWeight = {
  light:    300,
  regular:  400,
  medium:   500,
  semibold: 600,
  bold:     700,
}

export const tracking = {
  tight:   '-0.02em',
  snug:    '-0.01em',
  normal:   '0em',
  wide:     '0.05em',
  wider:    '0.1em',   // eyebrow labels
  widest:   '0.18em',  // section markers
}

export const lineHeight = {
  tight:   1.2,
  snug:    1.35,
  base:    1.5,
  relaxed: 1.65,
}

// Eyebrow style (mono uppercase section labels)
export const eyebrow: React.CSSProperties = {
  fontFamily: fontFamily.mono,
  fontSize:   '11px',
  fontWeight: 400,
  color:      '#9AA0AD',
  letterSpacing: '0.1em',
  textTransform: 'uppercase',
}

// ── Spacing (4px base) ────────────────────────────────────────────────────
export const spacing = {
  0:   '0px',
  px:  '1px',
  xs:  '4px',
  sm:  '8px',
  3:   '12px',
  md:  '16px',
  5:   '20px',
  lg:  '24px',
  8:   '32px',
  xl:  '32px',
  10:  '40px',
  xxl: '48px',
  12:  '48px',
  14:  '56px',
  16:  '64px',
}

// ── Border radius ─────────────────────────────────────────────────────────
export const radius = {
  none: '0px',
  sm:   '6px',
  md:   '10px',   // cards, inputs, buttons
  lg:   '14px',
  xl:   '20px',
  full: '9999px', // pills
}

// ── Shadows (border-first approach — very light) ──────────────────────────
export const shadow = {
  none: 'none',
  xs:   '0 1px 2px rgba(10,20,19,0.04)',
  sm:   '0 1px 4px rgba(10,20,19,0.06)',
  md:   '0 2px 8px rgba(10,20,19,0.08)',
  lg:   '0 4px 16px rgba(10,20,19,0.10)',
  xl:   '0 8px 24px rgba(10,20,19,0.12)',
}

// ── Focus ring ────────────────────────────────────────────────────────────
export const focusRing = 'var(--focus-ring)'

// ── Transitions ───────────────────────────────────────────────────────────
export const transition = {
  fast: '120ms ease',
  base: '170ms ease',
  slow: '280ms ease',
}

// ── Component sizes ───────────────────────────────────────────────────────
export const componentHeight = {
  btnSm:   '30px',
  btnMd:   '34px',
  btnLg:   '40px',
  inputSm: '30px',
  inputMd: '34px',
  inputLg: '40px',
  chip:    '22px',
  tag:     '20px',
}

// ── Layout ────────────────────────────────────────────────────────────────
export const layout = {
  sidebarWidth:  220,
  topbarHeight:  56,
  contentMaxWidth: 1200,
  contentPadding:  32,
}

// ── Breakpoints ───────────────────────────────────────────────────────────
export const breakpoints = {
  mobile: 768,
  tablet: 1024,
}

// ── Import type for eyebrow ───────────────────────────────────────────────
import type React from 'react'
